import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
  GetManyParams,
  GetManyResult,
  DeleteParams,
} from 'react-admin'
import { PRINT_LAYOUTS_URL } from '../../api-urls'
import {
  get,
  getBlob,
  getWithPagination,
  patch,
  post,
  remove,
} from '../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import { PrintLayoutDto } from '../../dto/print-layout.dto'
import filterMapper from './print-layouts-filter.mapper'
import { mapSortPrinterLaytoutsParam } from './print-layouts-sort.mapper'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<PrintLayoutDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortPrinterLaytoutsParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<PrintLayoutDto[]>(
      `${PRINT_LAYOUTS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<PrintLayoutDto>> => {
    const data = await get<PrintLayoutDto>(PRINT_LAYOUTS_URL, `/${id}`)
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource,
    { data }: CreateParams<CreatePrintLayoutRequest>,
  ): Promise<CreateResult<PrintLayoutDto>> => {
    const printLayout = await post<
      CreatePrintLayoutRequest[],
      PrintLayoutDto[]
    >(PRINT_LAYOUTS_URL, [data])
    return {
      data: printLayout[0],
    }
  },
  update: async (
    resource: string,
    { id, data }: UpdateParams,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      PrintLayoutDto
    >(PRINT_LAYOUTS_URL, {
      id,
      ...data,
    })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult> => {
    const deleted = await remove<PrintLayoutDto>(
      PRINT_LAYOUTS_URL,
      `/Delete/${id}`,
    )

    return Promise.resolve({ data: deleted })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<PrintLayoutDto>> => {
    const data = await get<PrintLayoutDto[]>(
      PRINT_LAYOUTS_URL,
      `/GetByConditions/o=>new int[] {${ids.toString()}}.Contains(o.Id)`,
    )
    return Promise.resolve({ data })
  },
  printTickets: async (
    resource: string,
    params: PrintTicketsParams,
  ): Promise<any> => {
    const blob = await getBlob(PRINT_LAYOUTS_URL, '/PrintTickets', 'POST', {
      ...params,
    })
    return Promise.resolve({
      data: {
        blob,
      },
    })
  },
} as PrintLayoutsDataProvider

interface PrintLayoutsDataProvider extends DataProvider {
  printTickets: (resource: string, params: PrintTicketsParams) => Promise<any>
}

export interface PrintTicketsParams {
  printLayoutId: number
  ticketsConditions?: string
}

export interface CreatePrintLayoutRequest {
  readonly name: string
  readonly width: number
  readonly height: number
  readonly backgroundImageId: number
  readonly printerDefinitionId: number
}

export default provider
